import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { LogOut, RefreshCw, Trash2 } from 'lucide-react';


import { useUserInfo } from '../hooks/useUserInfo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { formatDate } from '../lib/utils';
import { supabase } from '@/lib/supabase';
import { useGemBalance } from '@/hooks/useGemBalance';
import { isHidingMonetizationInStores } from '@/lib/detection';
import { USER_EVENTS, useTracking } from '@/hooks/useTracking';

export default function SettingsPage() {
  const { session, isLoading: isSessionLoading } = useSessionContext();
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  const { 
    userDetails,
    subscriptions, 
    isLoading, 
    updateUserDetails,
    isSubscriber,
    isUnlimited,
  } = useUserInfo();
  const { balance, refreshBalance } = useGemBalance();

  // Form state for personal info
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState<number | undefined>();
  const [ageError, setAgeError] = useState<string | null>(null);
  const [aboutMe, setAboutMe] = useState('');
  const [personalInfoModified, setPersonalInfoModified] = useState(false);
  const [isSavingPersonalInfo, setIsSavingPersonalInfo] = useState(false);
  
  // Form state for preferences
  // const [language, setLanguage] = useState('en');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [preferencesModified, setPreferencesModified] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // Form state for account deletion
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [accountDeletionSuccess, setAccountDeletionSuccess] = useState(false);
  const [accountDeletionError, setAccountDeletionError] = useState<string | null>(null);

  // Initialize form with user data
  useEffect(() => {
    if (userDetails) {
      setNickname(userDetails.usr_nickname || '');
      setGender(userDetails.usr_gender || '');
      setAge(userDetails.usr_age || undefined);
      setAboutMe(userDetails.usr_aboutme || '');
      
      // Get meta data if available
      if (userDetails.eh_usr_meta) {
        try {
          const meta = typeof userDetails.eh_usr_meta === 'string' 
            ? JSON.parse(userDetails.eh_usr_meta)
            : userDetails.eh_usr_meta;
          
          // setLanguage(meta.usr_language || 'en');
          setEmailOptIn(meta.email_optin !== false); // Default to true if not set
        } catch (error) {
          console.error('Failed to parse user meta data', error);
        }
      }
    }
  }, [userDetails]);

  // Redirect if not authenticated, otherwise refresh balance
  useEffect(() => {
    if (!isSessionLoading && !session) {
      navigate('/');
    }
    void refreshBalance();
  }, [isSessionLoading, session, navigate, refreshBalance]);

  // Validate age function
  const validateAge = (value: number | undefined): string | null => {
    if (value !== undefined) {
      if (value < 18) {
        return 'Unsafe content will be blocked. Please flag any inappropriate content in the app and delete existing chats.';
      } else if (value > 118) {
        return 'Age should be 118 or less.';
      }
    }
    return null;
  };

  // Handle save personal info
  const handleSavePersonalInfo = async () => {
    // Validate before saving
    const ageValidationError = validateAge(age);
    if (ageValidationError) {
      setAgeError(ageValidationError);
      // Let the user change age verification status (<18)
      // return;
    }
    
    setIsSavingPersonalInfo(true);
    
    try {
      await updateUserDetails({
        usr_nickname: nickname,
        usr_gender: gender,
        usr_aboutme: aboutMe,
        ...(typeof age === 'number' && { usr_age: Math.min(age, 10000) }),
        // Update content restrictions based on age
        ...(typeof age === 'number' && {
          ...(age < 18 ? {
            nsfw_pref: false,
            eh_usr_meta: {
              age18ConfirmedAt: null,
              notAge18DisclosedAt: new Date().toISOString(),
            }
          } : {
            // let user opt-in to NSFW content
            eh_usr_meta: {
              age18ConfirmedAt: new Date().toISOString(),
              notAge18DisclosedAt: null,
            }
          })
        })
      });
      
      setPersonalInfoModified(false);
    } catch (error) {
      console.error('Failed to save personal information', error);
    } finally {
      setIsSavingPersonalInfo(false);
    }
  };

  // Handle save preferences
  const handleSavePreferences = async () => {
    setIsSavingPreferences(true);
    
    try {
      // Prepare meta data
      const metaData = {
        ...(typeof userDetails?.eh_usr_meta === 'object' ? userDetails.eh_usr_meta : {}),
        // usr_language: language,
        email_optin: emailOptIn
      };
      
      await updateUserDetails({
        eh_usr_meta: metaData
      });
      
      setPreferencesModified(false);
    } catch (error) {
      console.error('Failed to save preferences', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // Handle cancel personal info
  const handleCancelPersonalInfo = () => {
    // Reset form to current values
    if (userDetails) {
      setNickname(userDetails.usr_nickname || '');
      setGender(userDetails.usr_gender || '');
      setAge(userDetails.usr_age || undefined);
      setAgeError(userDetails.usr_age ? validateAge(userDetails.usr_age) : null);
      setAboutMe(userDetails.usr_aboutme || '');
    }
    setPersonalInfoModified(false);
  };

  // Handle cancel preferences
  const handleCancelPreferences = () => {
    // Reset form to current values
    if (userDetails && userDetails.eh_usr_meta) {
      try {
        const meta = typeof userDetails.eh_usr_meta === 'string'
          ? JSON.parse(userDetails.eh_usr_meta)
          : userDetails.eh_usr_meta;
        
        // setLanguage(meta.usr_language || 'en');
        setEmailOptIn(meta.email_optin !== false);
      } catch (error) {
        console.error('Failed to parse user meta data', error);
      }
    }
    
    setPreferencesModified(false);
  };

  // Track form changes for personal info
  const handlePersonalInfoChange = () => {
    setPersonalInfoModified(true);
  };

  // Track form changes for preferences
  const handlePreferencesChange = () => {
    setPreferencesModified(true);
  };

  // Get active subscription information
  const activeSubscription = subscriptions.find(
    sub => sub.status === 'active' && sub.end_at && new Date(sub.end_at) > new Date()
  );

  // Check if there are any validation errors for personal info
  const hasPersonalInfoErrors = false; // ageError !== null;

  // Handle age blur event
  const handleAgeBlur = () => {
    const error = validateAge(age);
    setAgeError(error);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleAccountDeletion = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to delete your account?\n\n' +
      [
        'This action will: ' +
        '• Permanently delete your account and all data ' +
        '• Cancel any active subscriptions ' +
        '• Remove all chat history and characters\n\n'
      ].join('\n') +
      'This action cannot be undone and may take up to 72h to complete.\n\n' +
      'Click OK to proceed or Cancel to keep your account.'
    );

    if (!confirmed) return;

    setIsDeletingAccount(true);
    setAccountDeletionError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const { data, error } = await supabase.functions.invoke('eh-account-deletion', {method: 'POST'});
      
      trackEvent(USER_EVENTS.USER_ACCOUNT_DELETION_REQUESTED);

      if (error || !data?.success) {
        throw error || new Error('Failed to submit deletion request');
      }

      setAccountDeletionSuccess(true);
    } catch (error) {
      trackEvent(USER_EVENTS.USER_ACCOUNT_DELETION_ERROR, {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      setAccountDeletionError('Sorry, an error happened. Please send an email to request deletion at <a href="mailto:support@eveninghoney.ai?subject=Account%20Deletion%20Request">support@eveninghoney.ai</a>');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  if (isLoading || isSessionLoading) {
    return (
      <div className="flex-auto bg-zinc-800 text-white">
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold text-white mb-6">Profile Settings</h1>
            <div className="bg-zinc-900 rounded-lg p-6 animate-pulse">
              <div className="h-8 bg-zinc-800 rounded-md mb-4"></div>
              <div className="h-8 bg-zinc-800 rounded-md mb-4"></div>
              <div className="h-8 bg-zinc-800 rounded-md mb-4"></div>
              <div className="h-20 bg-zinc-800 rounded-md"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-auto bg-zinc-800 text-white">
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-6">Account Settings</h1>
          
          {/* Account Settings */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Personal Information</h2>
            
            <div className="space-y-4">
              {/* Email: TODO: allow editing */}
              <div>
                <Input 
                  id="email"
                  value={session?.user?.email}
                  className="bg-zinc-800 border-zinc-700 w-auto max-w-full field-sizing-content"
                  disabled
                />
              </div>

              <div>
                <label htmlFor="nickname" className="block text-sm font-medium mb-1">Nickname</label>
                <Input 
                  id="nickname"
                  value={nickname}
                  onChange={(e) => {
                    setNickname(e.target.value);
                    handlePersonalInfoChange();
                  }}
                  className="bg-zinc-800 border-zinc-700 w-auto max-w-full"
                />
              </div>
              
              <div>
                <label htmlFor="gender" className="block text-sm font-medium mb-1">Gender</label>
                <Select 
                  value={gender} 
                  onValueChange={(value) => {
                    setGender(value);
                    handlePersonalInfoChange();
                  }}
                >
                  <SelectTrigger id="gender" className="bg-zinc-800 border-zinc-700 w-32 max-w-full">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="unknown">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label htmlFor="age" className="block text-sm font-medium mb-1">Age</label>
                <Input 
                  id="age"
                  type="number"
                  min={18}
                  max={118}
                  value={age !== undefined ? age : ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : undefined;
                    setAge(value);
                    if (ageError) setAgeError(validateAge(value));
                    handlePersonalInfoChange();
                  }}
                  onBlur={handleAgeBlur}
                  className={`bg-zinc-800 border-zinc-700 w-32 max-w-full ${ageError ? 'border-red-500' : ''}`}
                />
                {ageError && (
                  <p className="text-red-500 text-xs mt-1">{ageError}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="aboutMe" className="block text-sm font-medium mb-1">About Me</label>
                <textarea 
                  id="aboutMe"
                  value={aboutMe}
                  onChange={(e) => {
                    setAboutMe(e.target.value);
                    handlePersonalInfoChange();
                  }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md"
                  rows={4}
                />
              </div>

              {personalInfoModified && (
                <div className="flex justify-end gap-2 mt-4">
                  <Button 
                    onClick={handleCancelPersonalInfo}
                    disabled={isSavingPersonalInfo}
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSavePersonalInfo}
                    disabled={isSavingPersonalInfo || hasPersonalInfoErrors}
                    className={`${hasPersonalInfoErrors ? 'bg-zinc-600 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-500'}`}
                  >
                    {isSavingPersonalInfo ? (
                      <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                    ) : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Subscription */}
          {!isHidingMonetizationInStores() && (
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Current Plan</h2>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-2">
              <div className="flex-1">
                <p className="text-amber-500 font-medium text-center md:text-left">
                  <span className="font-bold">{isSubscriber ? (isUnlimited ? 'Unlimited' : 'Premium') : 'Free'}</span> 
                  <span>{activeSubscription?.status === 'canceled' && ' (Canceled)'}</span>
                </p>
                {activeSubscription?.end_at && (
                  <p className="text-zinc-400 text-sm">
                    Subscription ends {formatDate(activeSubscription.end_at)}
                  </p>
                )}
                {!!activeSubscription?.gems_allocated && (
                  <p className="text-zinc-400 text-sm">
                    <span>Monthly gems included: </span>
                    <span className="font-bold">{activeSubscription.gems_allocated}</span>
                  </p>
                )}
                {!!activeSubscription && (
                  <p className="text-zinc-400 text-sm">
                    <span>Monthly gems remaining: </span>
                    <span className="font-bold">{balance.monthly}</span>
                    {/* <br />
                    <span className="font-bold">Total gems: </span>
                    <span>{balance.total}</span> */}
                  </p>
                )}
                {balance.purchased > 0.3 && (
                  <p className="text-zinc-400 text-sm">
                    <span>Purchased gems remaining: </span>
                    <span className="font-bold">{balance.purchased}</span>
                  </p>
                )}
              </div>
              <Button asChild className="bg-amber-600 hover:bg-amber-500">
                <a href="/subscription">{isSubscriber ? 'Manage Subscription' : 'Upgrade Now'}</a>
              </Button>
            </div>
          </div>
          )}
          
          {/* Preferences */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Preferences</h2>
            
            <div className="space-y-6">
              {/* <div>
                <label htmlFor="language" className="block text-sm font-medium mb-1">Language</label>
                <Select 
                  value={language} 
                  onValueChange={(value) => {
                    setLanguage(value);
                    handlePreferencesChange();
                  }}
                >
                  <SelectTrigger id="language" className="bg-zinc-800 border-zinc-700 max-w-full">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="emailOptIn" className="block text-sm font-medium mb-1">Receive email notifications</label>
                  <span className="text-zinc-400 text-sm">Receive updates, tips and special offers</span>
                </div>
                <Switch
                  id="emailOptIn"
                  checked={emailOptIn}
                  onCheckedChange={(checked) => {
                    setEmailOptIn(checked);
                    handlePreferencesChange();
                  }}
                />
              </div>
              
              {preferencesModified && (
                <div className="flex justify-end gap-2 mt-4">
                  <Button
                    onClick={handleCancelPreferences}
                    disabled={isSavingPreferences}
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSavePreferences}
                    disabled={isSavingPreferences}
                    className="bg-amber-600 hover:bg-amber-500"
                  >
                    {isSavingPreferences ? (
                      <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                    ) : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sign out */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Account Session</h2>
            <Button onClick={handleSignOut} className="">
              <LogOut className="h-5 w-5" />
              <span>Sign out</span>
            </Button>
          </div>

          {/* Account Deletion */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Account Deletion</h2>
            <div className="space-y-4">
              <div>
                <p className="text-zinc-300 text-sm">
                  <span>Permanently delete your account and all associated data according to our </span>
                  <Link to="/legal/privacy-policy" className="text-amber-500 hover:text-amber-400">Privacy Policy.</Link>
                </p>
                <p className="text-zinc-300 text-sm">
                  This action may take up to 72h to complete and cannot be undone.
                </p>
              </div>
              
              {accountDeletionSuccess ? (
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                  <p className="text-green-400 text-sm">
                    Account deletion request submitted successfully.
                  </p>
                  <p className="text-zinc-300 text-sm">
                    Our team will process your request within 72h and confirm completion via email.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {accountDeletionError && ( 
                    <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                      <p className="text-red-400 text-sm" dangerouslySetInnerHTML={{ __html: accountDeletionError }} />
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleAccountDeletion}
                    disabled={isDeletingAccount}
                    className="bg-red-600 hover:bg-red-500 disabled:bg-red-800"
                  >
                    {isDeletingAccount ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-5 w-5 mr-2" />
                        <span>Delete Account</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 